import type { JsonValue } from 'type-fest';

import { prepareAndMakeRequest } from '@frontend/api';

import { keysToSnakeCase } from '../utils/stringUtils';

const websocket = {
  init: (): Promise<{ socketToken: string }> =>
    prepareAndMakeRequest.post<{ socketToken: string }>('/websockets/init'),
};

export default {
  acceptTerms: (terms: string): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>(`/user_agreements/terms/${terms}`),

  getActiveUserAgreement: (termsType: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(
      `/user_agreements/terms/active/${termsType}`,
    ),

  getTerms: (termsType: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/user_agreements/terms/${termsType}`),

  getTokenAfterSSO: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/token`),

  login: (body: {
    password: string;
    username: string;
  }): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.post<{ token: { key: string; expiry: string } }>(
      '/login',
      { body },
    ),

  changePassword: (body: {
    currentPassword: string;
    newPassword: string;
    newPasswordConf: string;
  }): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.post<{ token: { key: string; expiry: string } }>(
      '/users/change_password',
      {
        body,
      },
    ),

  resetPassword: (body: { email: string }): Promise<string> =>
    prepareAndMakeRequest.post<string>('/forgot_password', { body }),

  requestLoginInfo: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>('/users/login_activity'),

  requestTwoFactorAuthSecret: (id: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/users/two_factor_auth/${id}`),

  verifyTwoFactorAuthToken: (
    id: string,
    token: string,
    secret: string,
  ): Promise<{ isValid: true }> =>
    prepareAndMakeRequest.post<{ isValid: true }>(
      `/users/two_factor_auth/${id}`,
      { body: { token, secret } },
    ),

  websocket,

  logoutAll: (): Promise<JsonValue> =>
    prepareAndMakeRequest.delete<JsonValue>('/users/log_out_all'),

  twoFactorLogin: ({
    username,
    password,
    token,
  }: {
    username: string;
    password: string;
    token: string;
  }): Promise<{ token: string }> =>
    prepareAndMakeRequest.post<{ token: string }>('/login', {
      body: { username, password, token },
    }),

  disableTwoFactorAuth: (userId: string): Promise<JsonValue> =>
    prepareAndMakeRequest.delete<JsonValue>(`/users/two_factor_auth/${userId}`),

  setPassword: (
    key: string,
    body: Record<string, JsonValue>,
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>(`/set_password/${key}`, { body }),

  profile: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>('/users/profile', {
      retryOptions: { maxRetries: 10 },
    }),

  updateUser: (
    username: string,
    body: { firstName: string; lastName: string },
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.put<JsonValue>(`/users/${username}`, { body }),

  // TODO: move to supplierService or something?
  getSupplierFileUrl: (
    groupId: string,
    token: string,
  ): Promise<{ url: string }> =>
    prepareAndMakeRequest.get<{ url: string }>(
      `/suppliers/${groupId}/download`,
      { query: { token } },
    ),

  signUp: (body: { isSigningUp: boolean }): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>('/registration', { body }),

  discoverSSO: (body: { email: boolean }): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>('/sso/discover', { body }),

  getUserGroups: (params: {
    search: string;
    pageNumber: string;
  }): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>('/users/super_users/user_groups', {
      query: params,
    }),

  updateActiveGroup: (
    userId: string,
    data: { activeGroupId: string },
  ): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.patch<{ token: { key: string; expiry: string } }>(
      `/users/super_users/${userId}/active_group`,
      { body: keysToSnakeCase(data) },
    ),

  getActiveUserGroupMembers: (): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(`/users/groups_members/`),

  switchActiveUserGroup: (
    nextGroupMemberId: string,
  ): Promise<{ token: { key: string; expiry: string } }> =>
    prepareAndMakeRequest.patch<{ token: { key: string; expiry: string } }>(
      `/users/active_group_member/`,
      { body: { groupMemberId: nextGroupMemberId } },
    ),
};

export const featureFlags = {
  get: (groupLocator: string): Promise<JsonValue> =>
    prepareAndMakeRequest.get<JsonValue>(
      `/users/groups/${groupLocator}/feature_flags/`,
    ),
  update: (
    groupLocator: string,
    data: Record<string, JsonValue>,
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.put<JsonValue>(
      `/users/groups/${groupLocator}/feature_flags/`,
      { body: keysToSnakeCase(data) },
    ),
};
